import type { Directive, DirectiveBinding } from 'vue'

let tooltipEl: HTMLDivElement | null = null

function createBodyTooltip(text: string) {
  if (!tooltipEl) {
    tooltipEl = document.createElement('div')
    tooltipEl.style.position = 'fixed'
    tooltipEl.style.background = 'var(--v-tooltip-background-color, #000)'
    tooltipEl.style.color = 'var(--v-tooltip-color, #fff)'
    tooltipEl.style.padding = 'var(--v-tooltip-padding, 6px 8px)'
    tooltipEl.style.borderRadius = 'var(--v-tooltip-border-radius, 4px)'
    tooltipEl.style.fontSize = 'var(--v-tooltip-font-size, 12px)'
    tooltipEl.style.zIndex = '2147483647'
    tooltipEl.style.pointerEvents = 'none'
    tooltipEl.style.transition = 'opacity 0.2s'
    tooltipEl.style.opacity = '0'
    tooltipEl.style.whiteSpace = 'nowrap'
    tooltipEl.style.overflow = 'hidden'
    document.body.appendChild(tooltipEl)
  }
  tooltipEl.textContent = text
}

function positionBodyTooltip(el: HTMLElement, value: any = {}) {
  if (!tooltipEl) { return }

  const rect = el.getBoundingClientRect()
  const placement = value?.theme?.placement || 'bottom'
  const offsetDirs = value?.theme?.offset || []
  const defaultOffset = 8

  let top = 0
  let left = 0
  let transform = ''

  let offsetX = defaultOffset
  let offsetY = defaultOffset

  if (offsetDirs.includes('left') || offsetDirs.includes('right')) {
    offsetX += el.scrollWidth - el.clientWidth
  }
  if (offsetDirs.includes('top') || offsetDirs.includes('bottom')) {
    offsetY += el.scrollHeight - el.clientHeight
  }

  switch (placement) {
    case 'top':
      top = rect.top - offsetY
      left = rect.left + rect.width / 2
      transform = 'translate(-50%, -100%)'
      break
    case 'bottom':
      top = rect.bottom + offsetY
      left = rect.left + rect.width / 2
      transform = 'translate(-50%, 0)'
      break
    case 'left':
      top = rect.top + rect.height / 2
      left = rect.left - offsetX
      transform = 'translate(-100%, -50%)'
      break
    case 'right':
      top = rect.top + rect.height / 2
      left = rect.right + offsetX
      transform = 'translate(0, -50%)'
      break
    default:
      top = rect.bottom + offsetY
      left = rect.left + rect.width / 2
      transform = 'translate(-50%, 0)'
  }

  tooltipEl.style.left = `${left}px`
  tooltipEl.style.top = `${top}px`
  tooltipEl.style.transform = transform
  tooltipEl.style.opacity = '1'
}

function removeBodyTooltip() {
  if (tooltipEl && tooltipEl.parentNode) {
    tooltipEl.parentNode.removeChild(tooltipEl)
  }
  tooltipEl = null
}

const d = {
  updateTooltip(el: HTMLElement, { value, modifiers }: DirectiveBinding<any>) {
    if (!value) { return }

    if (typeof value === 'string') {
      // we can pass either a string
      el.setAttribute('data-v-tooltip', value)

      // this check if when v-tooltip receives string with .arrow modifier
      if (modifiers.arrow) {
        el.style.setProperty('--v-tooltip-arrow-display', 'inline')
      }
    }
    else {
      // or an object
      if (value.text) {
        el.setAttribute('data-v-tooltip', value.text)
      }
      if (value.displayArrow || modifiers.arrow) {
        // if there is a prop global: true then mutate the :root css variables
        // otherwise it adds given variables to the element, which makes it possible to be different than others
        const targetEl = value.global ? document.documentElement : el
        targetEl.style.setProperty(
          '--v-tooltip-arrow-display',
          'inline',
        )
      }
      if (value.theme) {
        // if there is a prop global: true then mutate the :root css variables
        // otherwise it adds given variables to the element, which makes it possible to be different than others
        const targetEl = value.global ? document.documentElement : el
        for (const [key, val] of Object.entries(value.theme)) {
          if (key === 'placement') {
            switch (val) {
              case 'top':
                targetEl.style.setProperty(
                  '--v-tooltip-left',
                  '50%',
                )
                targetEl.style.setProperty(
                  '--v-tooltip-top',
                  '0%',
                )
                targetEl.style.setProperty(
                  '--v-tooltip-translate',
                  'translate(-50%, -110%)',
                )
                if (value.displayArrow || modifiers.arrow) {
                  targetEl.style.setProperty(
                    '--v-tooltip-arrow-border-color',
                    'var(--v-tooltip-background-color) transparent transparent transparent',
                  )
                  targetEl.style.setProperty(
                    '--v-tooltip-arrow-top',
                    'calc(var(--v-tooltip-top) - var(--v-tooltip-top-offset) + 8px)',
                  )
                }
                break
              case 'bottom':
                targetEl.style.setProperty(
                  '--v-tooltip-left',
                  '50%',
                )
                targetEl.style.setProperty(
                  '--v-tooltip-top',
                  '100%',
                )
                targetEl.style.setProperty(
                  '--v-tooltip-translate',
                  'translate(-50%, 10%)',
                )
                if (value.displayArrow || modifiers.arrow) {
                  targetEl.style.setProperty(
                    '--v-tooltip-arrow-border-color',
                    'transparent transparent var(--v-tooltip-background-color) transparent',
                  )
                  targetEl.style.setProperty(
                    '--v-tooltip-arrow-top',
                    'calc(var(--v-tooltip-top) - var(--v-tooltip-top-offset) - 7px)',
                  )
                }
                break
              case 'left':
                targetEl.style.setProperty(
                  '--v-tooltip-left',
                  '0%',
                )
                targetEl.style.setProperty(
                  '--v-tooltip-top',
                  '50%',
                )
                targetEl.style.setProperty(
                  '--v-tooltip-translate',
                  'translate(-110%, -50%)',
                )
                if (value.displayArrow || modifiers.arrow) {
                  targetEl.style.setProperty(
                    '--v-tooltip-arrow-border-color',
                    'transparent transparent transparent var(--v-tooltip-background-color)',
                  )
                  targetEl.style.setProperty(
                    '--v-tooltip-arrow-top',
                    'calc(var(--v-tooltip-top)',
                  )
                  targetEl.style.setProperty(
                    '--v-tooltip-arrow-left',
                    'calc( var(--v-tooltip-left) - var(--v-tooltip-left-offset) + 1.5px)',
                  )
                }
                break
              case 'right':
                targetEl.style.setProperty(
                  '--v-tooltip-left',
                  '100%',
                )
                targetEl.style.setProperty(
                  '--v-tooltip-top',
                  '50%',
                )
                targetEl.style.setProperty(
                  '--v-tooltip-translate',
                  'translate(10%, -90%)',
                )
                if (value.displayArrow || modifiers.arrow) {
                  targetEl.style.setProperty(
                    '--v-tooltip-arrow-border-color',
                    'transparent var(--v-tooltip-background-color) transparent  transparent',
                  )
                  targetEl.style.setProperty(
                    '--v-tooltip-arrow-top',
                    'calc(var(--v-tooltip-top)',
                  )
                  targetEl.style.setProperty(
                    '--v-tooltip-arrow-left',
                    'calc( var(--v-tooltip-left) - var(--v-tooltip-left-offset) - 2px)',
                  )
                }
                break
              default:
                break
            }
          }
          else if (key === 'offset' && !value.global) {
            for (const direction of (val as any)) {
              if (direction === 'left') {
                targetEl.style.setProperty(
                  '--v-tooltip-left-offset',
                  `-${targetEl.scrollWidth
                  - targetEl.clientWidth
                  }px`,
                )
              }
              else if (direction === 'right') {
                targetEl.style.setProperty(
                  '--v-tooltip-left-offset',
                  `${targetEl.scrollWidth
                  - targetEl.clientWidth
                  }px`,
                )
              }
              else if (direction === 'top') {
                targetEl.style.setProperty(
                  '--v-tooltip-top-offset',
                  `-${targetEl.scrollHeight
                  - targetEl.clientHeight
                  }px`,
                )
              }
              else if (direction === 'bottom') {
                targetEl.style.setProperty(
                  '--v-tooltip-top-offset',
                  `${targetEl.scrollHeight
                  - targetEl.clientHeight
                  }px`,
                )
              }
            }
          }
          else {
            targetEl.style.setProperty(`--v-tooltip-${key}`, (val as any))
          }
        }
      }
    }
  },
  // hooks
  mounted(el, { value, dir, modifiers }: DirectiveBinding<any>) {
    const isGlobal = typeof value === 'object' && value.global
    const isBody = typeof value === 'string' || (typeof value === 'object' && !value.global)
    const isString = typeof value === 'string'

    // to applay the tooltip to the element, pass the global as true
    if ((isString || (typeof value === 'object' && value.text)) && isGlobal) {
      el.classList.add('data-v-tooltip')
    }

    // adding tooltip to the body by default
    if (isBody) {
      el.addEventListener('mouseenter', () => {
        const text = isString ? value : value?.text
        if (text) {
          createBodyTooltip(text)
          positionBodyTooltip(el, value)
        }
      })
      el.addEventListener('mouseleave', removeBodyTooltip)
      el.addEventListener('mousemove', () => {
        positionBodyTooltip(el, value)
      })
    }

    (dir as any).updateTooltip(el, { value, modifiers })
  },
  updated(el, { value, dir, modifiers }) {
    const isGlobal = typeof value === 'object' && value.global
    const isString = typeof value === 'string'

    // apply the tooltip to body and remove the inline by default.
    if ((isString || (typeof value === 'object' && value.text)) && isGlobal) {
      el.classList.add('data-v-tooltip')
    }
    else {
      el.classList.remove('data-v-tooltip')
    }

    (dir as any).updateTooltip(el, { value, modifiers })
  },
  unmounted() { // remove the tooltips from body
    removeBodyTooltip()
  },
} as Directive<HTMLElement>

export default d
